import { ApiError } from '@/lib/api';

import type { StudySubjectAvailability } from './studyAvailability';
import { helpLevelLabel } from './studyHelpLevels';

/**
 * Learner messages for Study refusals (study-institution-m1-design.md §3.5, §11). Every message
 * says what happened and what still works, instead of a generic error.
 */

function detailsOf(error: ApiError): Record<string, unknown> {
  return error.details && typeof error.details === 'object'
    ? (error.details as Record<string, unknown>)
    : {};
}

/** Local wall-clock time for a resume instant, e.g. "Tue 7 Oct, 11:00 am". */
export function formatResumeTime(iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

function examWindowMessage(resumesAt: unknown, timeZone?: string): string {
  return typeof resumesAt === 'string'
    ? `AI Tutor is paused for an assessment until ${formatResumeTime(resumesAt, timeZone)}. You can still use tools and practice.`
    : 'AI Tutor is paused for an assessment. You can still use tools and practice.';
}

/** Returns learner copy for a known Study refusal, or null for any other error. */
export function studyAccessMessage(error: unknown, timeZone?: string): string | null {
  if (!(error instanceof ApiError)) return null;
  const details = detailsOf(error);
  switch (error.code) {
    case 'STUDY_ENTITLEMENT_REQUIRED':
      return 'Ask your parent to activate Study access for you in Parent Portal.';
    case 'INSTITUTION_AI_TOOL_DISABLED':
      return details.tool === 'study_tutor'
        ? 'Your school has turned off AI Tutor. You can still use tools and practice.'
        : 'Your school has turned off this AI tool.';
    case 'STUDY_TUTOR_DISABLED_BY_INSTITUTION':
      return 'Your school has turned off AI Tutor. You can still use tools and practice.';
    case 'STUDY_TUTOR_DISABLED_BY_CLASS':
      return 'Your teacher has turned off AI Tutor for this class. You can still use tools and practice.';
    case 'STUDY_TUTOR_EXAM_WINDOW':
      return examWindowMessage(details.resumes_at, timeZone);
    case 'STUDY_HINT_LEVEL_DISABLED_BY_INSTITUTION':
      return typeof details.max_hint_level === 'number'
        ? `Your teacher allows help up to “${helpLevelLabel(details.max_hint_level)}”. Choose another help button.`
        : 'Your teacher has limited the help level. Choose a lower help level.';
    case 'STUDY_H4_DISABLED_BY_PARENT':
      return 'Worked examples are off. A parent can change this.';
    case 'STUDY_HINT_LEVEL_DISABLED_BY_POLICY':
      return typeof details.max_hint_level === 'number'
        ? `Study currently allows help up to “${helpLevelLabel(details.max_hint_level)}”. Choose another help button.`
        : 'Choose a lower help level.';
    case 'STUDY_SUBJECT_DISABLED_BY_INSTITUTION':
    case 'STUDY_TOOL_DISABLED_BY_INSTITUTION':
      return 'Your school has turned this off.';
    case 'STUDY_SOURCE_CONFIRMATION_REQUIRED':
      return 'Check the text from your schoolwork and confirm it first. Then you can use AI Tutor.';
    case 'STUDY_SOURCE_CHANGED':
      return 'You picked a different page while confirming. Check the text for this page and confirm again.';
    case 'STUDY_UPLOADS_DISABLED_BY_INSTITUTION':
      return 'Your school has turned off uploads in Study.';
    case 'STUDY_SUBJECT_CAPABILITY_UNAVAILABLE':
      return 'This subject does not support this Study activity right now.';
    case 'INSUFFICIENT_POOL_BALANCE':
      return 'Your school has run out of Stars for AI help. Ask your teacher.';
    // Study licence (SIR-COM-01). The server sends these only after a school's licence checks are
    // switched on; in the default grace period students never see them.
    case 'STUDY_LICENCE_REQUIRED':
      return 'Your school does not have a Study licence yet. Ask your teacher.';
    case 'STUDY_LICENCE_EXPIRED':
      return "Your school's Study licence has ended. Ask your teacher. Your work is saved.";
    case 'STUDY_LICENCE_SEATS_EXHAUSTED':
      return "All of your school's Study seats are in use. Ask your teacher.";
    case 'STUDY_TRIAL_CLASS_REQUIRED':
      return "Your school's Study trial is for a different class. Ask your teacher.";
    // Study consent (SIR-TRU-10): the school recorded that consent for Study was withdrawn.
    case 'STUDY_CONSENT_WITHDRAWN':
      return 'Study is not available for your account right now. Please talk to your school.';
    // Institution assignments (SIR-TEA-01).
    case 'STUDY_ASSIGNMENT_NOT_FOUND':
      return 'This task is not available to you any more.';
    case 'STUDY_ASSIGNMENT_CLOSED':
      return 'Your teacher has closed this task. Your work so far is saved.';
    case 'STUDY_ASSIGNMENT_NOT_STARTED':
    case 'STUDY_ASSIGNMENT_VISIBILITY_ACK_REQUIRED':
      return 'Start the task first so your teacher can see this work.';
    case 'STUDY_ASSIGNMENT_TARGET_MISMATCH':
      return 'This answer is not part of the task. Open the task again from Study.';
    case 'STUDY_ASSIGNMENT_PROGRESS_FAILED':
      return 'Your work was saved, but the task progress did not update. Try again.';
    default:
      return null;
  }
}

/** Banner copy for a subject whose Tutor is not available, or null when Tutor is available. */
export function tutorStateMessage(
  subject: StudySubjectAvailability | null,
  timeZone?: string,
): string | null {
  if (!subject) return null;
  switch (subject.tutor.state) {
    case 'available':
      return null;
    case 'disabled_platform':
      return 'AI Tutor is not available for this subject right now. You can still use tools and practice.';
    case 'disabled_institution':
      return subject.enabled
        ? 'Your school has turned off AI Tutor. You can still use tools and practice.'
        : 'Your school has turned this subject off.';
    case 'disabled_class':
      return 'Your teacher has turned off AI Tutor for this class. You can still use tools and practice.';
    case 'exam_window':
      return examWindowMessage(subject.tutor.resumes_at, timeZone);
  }
}

/** Shown on the Study home page when Study is not meant for this learner (SIR-HYG-01). */
export function studyNotAvailableMessage(learnerSource: 'family' | 'institution' | 'walk_in') {
  if (learnerSource === 'institution') return 'Your school has not turned on Study for you yet.';
  if (learnerSource === 'walk_in') return 'Study opens when you sign in with your own account.';
  return 'Study is not available for your account right now. Ask a parent for help.';
}
